import FormatedText from "@/components/FormatedText";
import { useComponent } from "@/hooks/useMicrositeComponent";
import { cn } from "@/lib/utils";
import { MComponentSpoilerProps } from "@/types/microsite";
import { ChevronDownIcon, LockClosedIcon } from "@heroicons/react/24/solid";
import { animated, easings, useSpring } from "@react-spring/web";
import dynamic from "next/dynamic";
import { Fragment, Suspense, useEffect, useState } from "react";
import { useMeasure } from "react-use";
import { useFontRender } from "../../FontRender";
import {
  DummyIcon,
  LinkComponentStyle,
  StyledIcon,
  StyledLink,
  TextOuter,
} from "./LinkComponents";
import { useProtected } from "./ProtectedModal";

import { TextComponentStyle } from "./Text";

const RenderIcon = dynamic(() => import("@/components/RenderIcon"), {
  ssr: false,
});

export interface MComponentSpoilerButtonProps extends MComponentSpoilerProps {
  needPassword: boolean;
  microsite: any;
  isOpen: boolean;
  onToggle;
  isBuilder: boolean;
  id: string;
  children: React.FC<any>;
  passwordMessage: string;
  onProtectedOpen: (data: any) => void;
}

export const SpoilerButton = ({
  needPassword: _needPassword,
  isSpoiler,
  microsite,
  spoilerButtonColor,
  spoilerButtonIcon,
  spoilerButtonText,
  spoilerButtonTextPosition,
  spoilerButtonFont,
  isOpen,
  onToggle,
  isBuilder,
  id,
  children,
  passwordMessage = "",
  onProtectedOpen,
  isFocus,
}) => {
  const [needPassword, setNeedPassword] = useState(_needPassword);
  const spoilerButtonType = microsite?.style?.buttonType || "elevate";
  const withIcon = !!spoilerButtonIcon;
  const { focusTo } = useComponent();

  const { fontStyle, RenderFontStyle } = useFontRender({
    textFont: spoilerButtonFont,
  });

  const protectedProp = useProtected({
    micrositeId: microsite?.linkId,
    componentId: id,
    message: passwordMessage,
    isBuilder,
    onSuccess: (data) => {
      onToggle();
      onProtectedOpen(data);
      setNeedPassword(false);
    },
  });

  const handleToggle = (e) => {
    e.preventDefault();
    if (isBuilder) {
      focusTo(id);
    }
    if (needPassword) {
      protectedProp.onRequest();
      return;
    }
    onToggle();
  };

  const hasButtonShadow = !!microsite?.style?.buttonShadowColor;

  useEffect(() => {
    if (isFocus) {
      setTimeout(() => {
        handleToggle({
          preventDefault: () => {
            //...
          },
        });
      }, 1500);
    }
  }, [isFocus]);

  if (!isSpoiler) return <Fragment>{children}</Fragment>;

  return (
    <Fragment>
      {RenderFontStyle}
      <LinkComponentStyle id={`mcomp-${id}`}>
        <StyledLink
          onClick={handleToggle}
          buttonType={spoilerButtonType}
          buttonEffect={""}
          buttonColor={spoilerButtonColor}
          hasButtonShadow={hasButtonShadow}
          isButton
        >
          {withIcon ? (
            <StyledIcon buttonType={spoilerButtonType} className="">
              <Suspense>
                <RenderIcon
                  icon={spoilerButtonIcon}
                  iconSize="var(--icon-width)"
                />
              </Suspense>
            </StyledIcon>
          ) : (
            <DummyIcon textPosition={spoilerButtonTextPosition} left />
          )}
          <TextOuter className="">
            <TextComponentStyle
              style={fontStyle}
              position={spoilerButtonTextPosition}
            >
              <FormatedText value={spoilerButtonText || "Spoiler"} singleLine />
            </TextComponentStyle>
          </TextOuter>

          {needPassword ? (
            <StyledIcon buttonType={spoilerButtonType} className="">
              <LockClosedIcon className="w-5" />
            </StyledIcon>
          ) : (
            <StyledIcon buttonType={spoilerButtonType} className="">
              <ChevronDownIcon
                className={cn("mt-0.5 w-6 sm:w-8 transition-all", {
                  "rotate-180": isOpen,
                })}
              />
            </StyledIcon>
          )}
        </StyledLink>
      </LinkComponentStyle>

      <SpoilerChildren isOpen={isOpen} spoilerButtonType={spoilerButtonType}>
        {children}
      </SpoilerChildren>
    </Fragment>
  );
};

const SpoilerChildren = ({
  isOpen,
  duration = 750,
  children,
  spoilerButtonType,
}) => {
  const [ref, { height }] = useMeasure();

  const propStyle = useSpring({
    height: isOpen ? height + 35 : 0,
    config: {
      duration,
      easing: easings.easeOutExpo,
    },
  });

  return (
    <animated.div
      style={{ ...propStyle, overflow: "hidden" }}
      className="mx-2 -mt-2 w-full"
    >
      <animated.div
        ref={ref as any}
        className={cn(
          "text-gray-900 bg-white rounded-lg mt-3 overflow-hidden",
          spoilerButtonType === "full-round" && "mt-4"
        )}
      >
        {children}
      </animated.div>
    </animated.div>
  );
};
